'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { signInWithCustomToken } from 'firebase/auth';
import { getFirebaseAuth } from '@/lib/firebase';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding-context';
import { landingPathFor } from '@/lib/landing';

const SIGNUP_ENABLED = process.env.NEXT_PUBLIC_SIGNUP_ENABLED === 'true';

export function LoginForm() {
  const { user, ownerSession, bootTenant, loading, signInWithGoogle, error, setError } = useAuth();
  const branding = useBranding();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [ctLoading, setCtLoading] = useState(false);
  const [isWebView, setIsWebView] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent;
    const webview =
      /FBAN|FBAV|Instagram|ZaloApp|Zalo\/|MicroMessenger|Line\/|Twitter\/|TikTok/i.test(ua) ||
      (/Android/i.test(ua) && /wv\b/.test(ua)) ||
      (/iPhone|iPad/i.test(ua) && !/Safari\//i.test(ua) && /AppleWebKit/i.test(ua));
    setIsWebView(webview);
  }, []);

  // Cross-app SSO: ?ct=customToken (từ GBC hoặc app khác trong hệ sinh thái)
  useEffect(() => {
    const ct = searchParams.get('ct');
    if (!ct || loading || user) return;
    setCtLoading(true);
    setError(null);
    signInWithCustomToken(getFirebaseAuth(), ct).catch(() => {
      setCtLoading(false);
      setError('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
    });
  }, [searchParams, loading, user, setError]);

  // Redirect sau khi login thành công — CTV không có quyền /dashboard, đưa thẳng vào Timeline.
  // Owner Portal v2 (2026-10-11): chủ nhà KHÔNG có `user` (không có gmsRole) nên nhánh trên luôn bỏ
  // qua — thiếu nhánh `ownerSession` này khiến chủ nhà đăng nhập THÀNH CÔNG THẬT (confirm qua Network:
  // /api/owner/profile trả 200) nhưng đứng yên mãi ở màn đăng nhập, không ai điều hướng đi đâu cả. Đưa
  // thẳng vào `/timeline` (route DUY NHẤT luôn an toàn cho owner dù hạng mục nào được cấp — tự kiểm
  // `ownerSession` ngay đầu, xem app/(app)/timeline/page.tsx — tự báo "chưa được cấp quyền" nếu owner
  // không có hạng mục `bookings`, không crash).
  useEffect(() => {
    if (loading) return;
    if (user) { router.replace(landingPathFor(user.role, bootTenant)); return; }
    if (ownerSession) router.replace('/timeline');
  }, [user, ownerSession, bootTenant, loading, router]);

  if (loading || ctLoading || (!loading && (user || ownerSession))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-600 to-emerald-500 animate-pulse" />
          <p className="text-sm text-gray-500">Đang tải...</p>
        </div>
      </div>
    );
  }

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch {
      // error đã set trong auth context
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 px-4 py-10">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-white dark:from-orange-900/20 dark:via-transparent dark:to-transparent pointer-events-none" />

      <div className="relative w-full max-w-sm animate-fade-in space-y-4">

        {/* Logo */}
        <div className="text-center mb-2">
          <div className="flex justify-center mb-3">
            <div className="bg-orange-50 rounded-2xl px-6 py-3 shadow-sm text-gray-800">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={branding?.logoUrl || '/logo-gms.svg'} alt={branding?.name || 'Gold Management System'} className="h-[42px] w-auto" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-[var(--foreground)]">{branding ? branding.name : 'Gold Management System'}</h1>
          <p className="text-sm text-[var(--muted)] mt-1">{branding ? 'Hệ thống quản lý căn hộ dịch vụ' : 'Gold standard hospitality, AI-automated. More revenue. Less management.'}</p>
        </div>

        {/* Login card */}
        <div className="bg-[var(--card)] rounded-2xl shadow-xl border border-[var(--border)] p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Đăng nhập
          </p>

          {error && (
            <div className="mb-3 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400 text-xs">
              <span className="mt-0.5 shrink-0">🚫</span>
              <span>{error}</span>
            </div>
          )}

          {isWebView ? (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs">
                <span className="mt-0.5 shrink-0">⚠️</span>
                <span>
                  Google không cho phép đăng nhập trong trình duyệt nhúng (Zalo, Facebook...).
                  Vui lòng mở trang này trong <strong>Safari</strong> hoặc <strong>Chrome</strong>.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(window.location.href).catch(() => { /* clipboard bị chặn / không có quyền — miễn trừ NO-SILENT, không phải lỗi hệ thống */ });
                }}
                className="w-full py-2.5 rounded-xl border border-[var(--border)] bg-[var(--background)] text-[var(--foreground)] text-sm font-medium transition active:scale-[0.98]"
              >
                Copy link để mở trình duyệt khác
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleGoogle}
              disabled={googleLoading}
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border-2 border-[var(--border)] hover:border-gray-300 dark:hover:border-gray-600 bg-[var(--background)] hover:bg-gray-50 dark:hover:bg-gray-800/50 text-[var(--foreground)] font-medium text-sm transition active:scale-[0.98] disabled:opacity-60"
            >
              {googleLoading ? (
                <span className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-brand-600 animate-spin" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              {googleLoading ? 'Đang đăng nhập...' : 'Đăng nhập với Google'}
            </button>
          )}
        </div>

        {SIGNUP_ENABLED && !branding && (
          <p className="text-center text-xs text-[var(--muted)]">
            Chưa có tài khoản? <Link href="/signup" className="text-brand-600 hover:underline font-medium">Tạo miễn phí</Link>
          </p>
        )}

        <p className="text-center text-xs text-gray-400">
          Gold Management System · © LEDIFA
        </p>
      </div>
    </div>
  );
}
